import { StrictMode, startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { StartClient } from '@tanstack/react-start/client';
import { extractErrorInfo, logErrorFn } from './lib/data';
import { shouldReportFrontendError } from './lib/frontend-error-filter';
import { recentBreadcrumbs } from './lib/session-actions';
import { isVersionSkewReloadInProgress } from './routes/-components/default-catch-boundary';

/**
 * Custom client entry, auto-detected as `src/client` and replacing the package default (see
 * `resolveEntry` in @tanstack/start-plugin-core). It exists for exactly one reason: to pass React's
 * error hooks — `onRecoverableError` and `onUncaughtError` — to `hydrateRoot`, so a hydration failure
 * is reported with the `componentStack` that names our own components instead of only React internals.
 *
 * Everything else is a verbatim copy of the default entry — `startTransition`, `StrictMode`, hydrating
 * `document`. Keep it that way, or hydration behaviour drifts from upstream for no reason.
 */

/**
 * React error numbers meaning "the server markup and the client's first render disagreed". 418 is the
 * mismatch itself (`text` or `HTML`); the others are the neighbouring hydration failures React can
 * report while recovering the same tree.
 */
const HYDRATION_ERROR_NUMBERS = new Set([418, 421, 422, 423, 425]);

function isHydrationError(message: string): boolean {
	const number = /minified react error #(\d+)/i.exec(message)?.[1];
	if (number) return HYDRATION_ERROR_NUMBERS.has(Number(number));
	return message.toLowerCase().includes('hydrat');
}

/**
 * Report the errors React *recovers* from, above all the hydration mismatch behind the
 * `PORTAL_FRONTEND_ERROR: … Minified React error #418` tickets (TEC-3651, TEC-3655).
 *
 * Those tickets are undiagnosable as reported: in a production build the message is `Minified React
 * error #418` and the only stack is React's own internals inside `vendor-react-*.js`, so nothing says
 * WHICH text in WHICH component. `onRecoverableError` is the only place the missing detail exists —
 * React hands it an `errorInfo` carrying a `componentStack`, and every chunk ships a sourcemap, so those
 * frames resolve to real component names.
 *
 * Why this does not double-report: React's DEFAULT `onRecoverableError` calls `reportError(error)`,
 * which dispatches a window `error` event — what the global handler in `router.tsx` has been picking up
 * all along. Supplying our own replaces that call, so each recoverable error is reported once, here,
 * with the component stack. The `console.error` preserves the visibility `reportError` used to give.
 *
 * Scope worth knowing: `StartClient` renders the router behind an `Await`, so the app subtree is
 * client-rendered rather than hydrated and a divergence inside it raises nothing. What this catches is
 * the hydrated shell — the `<html>`/`<body>` tree in `__root.tsx` — which is where the production #418s
 * have to be coming from.
 *
 * The component stack rides in `stack`, never in `message`: the system-error reporter fingerprints on
 * the normalized message, so a per-occurrence component path there would mint a fresh ticket for every
 * distinct component and bury the board.
 */
function reportWithComponentStack(
	resolvePrefix: (message: string) => string,
	error: unknown,
	errorInfo: { componentStack?: string | null },
): void {
	// A stale-chunk reload is already in flight; whatever React recovered from is a symptom of the
	// version skew, not a bug. Same guard as the global handlers in `router.tsx`.
	if (isVersionSkewReloadInProgress()) return;

	const info = extractErrorInfo(error);
	const prefix = resolvePrefix(info.message);
	console.error(prefix, info.name, info.message, errorInfo.componentStack);

	// Environmental noise reaches this path too — a page translator mutating the DOM makes React recover
	// exactly like a genuine mismatch does — so it goes through the same filter as everything else.
	if (!shouldReportFrontendError(info.name, info.message, info.stack)) return;

	const componentStack = errorInfo.componentStack?.trim();
	logErrorFn({
		data: {
			route: window.location.pathname,
			name: prefix + ' ' + info.name,
			message: info.message,
			stack: componentStack ? `${info.stack ?? '(no stack)'}\n\nComponent stack:${componentStack}` : info.stack,
			// Same shape the boundary and the two global handlers send, so this path is not the odd one out
			// in telemetry. Breadcrumbs are usually near-empty here (a mismatch happens during load, before
			// the visitor has done anything), which is itself a useful signal.
			source: 'recoverable',
			breadcrumbs: recentBreadcrumbs(),
		},
	}).catch((logErr) => {
		console.error('Failed to log error to backend:', logErr);
	});
}

function reportRecoverableError(error: unknown, errorInfo: { componentStack?: string | null }): void {
	reportWithComponentStack(
		(message) => (isHydrationError(message) ? '[HYDRATION]' : '[RECOVERABLE]'),
		error,
		errorInfo,
	);
}

/**
 * Report the errors React does NOT recover from and no boundary caught.
 *
 * Without this they take React's default `onUncaughtError`, which calls `reportError(error)` and so
 * surfaces as a window `error` event — picked up by the global handler in `router.tsx`, which has no
 * access to React's `errorInfo` and therefore reports **no component stack**. That is exactly why
 * TEC-3690 (`Minified React error #418 … args[]=HTML`, a structural hydration mismatch) arrived
 * undiagnosable: React-internal frames in `vendor-react-*.js` and nothing naming our own elements.
 * Sourcemaps cannot help there, because the frames genuinely belong to the reconciler.
 *
 * Supplying this handler replaces that `reportError` call, so the failure is reported ONCE — here, with
 * the component stack — rather than once from the window handler without it.
 *
 * Kept under its own `[UNCAUGHT]` prefix rather than reusing `[HYDRATION]`: the system-error reporter
 * fingerprints on the message prefix, and an error React could not recover from is a different severity
 * from one it patched over. Merging them would hide a broken page behind a ticket for a cosmetic
 * mismatch.
 */
function reportUncaughtError(error: unknown, errorInfo: { componentStack?: string | null }): void {
	reportWithComponentStack(() => '[UNCAUGHT]', error, errorInfo);
}

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<StartClient />
		</StrictMode>,
		// `onCaughtError` is deliberately left at React's default: an error a boundary caught is already
		// reported by that boundary (see `DefaultCatchBoundary`), so handling it here would double-report.
		{ onRecoverableError: reportRecoverableError, onUncaughtError: reportUncaughtError },
	);
});
